import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react';

const ExerciseFormContext = createContext();

const initialExerciseFormState = {
  form: {
    milestoneId: null,
    title: '',
    titleAudioKey: '',
    key: '',
    order: '',
    newWords: null,
    // coachNotes: '',
    coachNotesIntro: '',
    coachNotesSteps: '',
    // beforeExercise
    beforeExerciseText: '',
    beforeExerciseTextAudioKey: '',
    beforeExerciseInstructionsVideoUrl: '',
    beforeExerciseTip: '',
    beforeExerciseTipAudioKey: '',
  },
  httpError: '',
  validationErrs: {},
  loading: false,
  submitLoading: false,
  isDirty: false,
};

export const exerciseFormActionTypes = {
  UPDATE_FORM_FIELDS: 'UPDATE_FORM_FIELDS',
  UPDATE_FORM_META_DATA: 'UPDATE_FORM_META',
  RESET_FORM: 'RESET_FORM',
};

function exerciseFormReducer(state, action) {
  switch (action.type) {
    case exerciseFormActionTypes.UPDATE_FORM_FIELDS: {
      return {
        ...state,
        form: { ...state.form, ...action.data },
      };
    }
    case exerciseFormActionTypes.UPDATE_FORM_META_DATA: {
      return { ...state, ...action.data };
    }
    case exerciseFormActionTypes.RESET_FORM: {
      return { ...initialExerciseFormState };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export function ExerciseFormProvider({ children }) {
  const [state, dispatch] = useReducer(
    exerciseFormReducer,
    initialExerciseFormState
  );

  const value = [state, dispatch];

  return (
    <ExerciseFormContext.Provider value={value}>
      {children}
    </ExerciseFormContext.Provider>
  );
}

export function useExerciseForm() {
  const [state, dispatch] = useContext(ExerciseFormContext);

  if (!state) {
    throw new Error(
      'useExerciseForm must be used within the ExerciseFormContext'
    );
  }

  // Show browser's default warning on reload without saving
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (state.isDirty) {
        event.preventDefault();
        event.returnValue = ''; // This triggers the browser warning
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state.isDirty]);

  const updateFormFields = useCallback(
    (data, isDirty = true) => {
      dispatch({ type: exerciseFormActionTypes.UPDATE_FORM_FIELDS, data });
      dispatch({
        type: exerciseFormActionTypes.UPDATE_FORM_META_DATA,
        data: { isDirty },
      });
    },
    [dispatch]
  );

  const updateFormMetaData = useCallback(
    (data) => {
      dispatch({ type: exerciseFormActionTypes.UPDATE_FORM_META_DATA, data });
    },
    [dispatch]
  );

  const resetForm = useCallback(() => {
    dispatch({ type: exerciseFormActionTypes.RESET_FORM });
  }, [dispatch]);

  return {
    state,
    updateFormFields,
    updateFormMetaData,
    resetForm,
  };
}
